import { motion, AnimatePresence } from 'framer-motion';
import { useKeyboardShortcutsContext } from '../../contexts/KeyboardShortcutsContext';
import { HiCommandLine, HiCheckCircle } from 'react-icons/hi2';

export const SequenceIndicator = () => {
  const { sequenceKeys, sequenceStatus, successDestination } = useKeyboardShortcutsContext();

  const formatKey = (key: string): string => {
    const keyMap: Record<string, string> = {
      'ctrl': '⌃',
      'control': '⌃',
      'alt': '⌥',
      'option': '⌥',
      'shift': '⇧',
      'cmd': '⌘',
      'command': '⌘',
      'meta': '⌘',
      'enter': '↵',
      'return': '↵',
      'backspace': '⌫',
      'delete': '⌦',
      'escape': 'Esc',
      'esc': 'Esc',
      'tab': '⇥',
      'space': '␣',
      'up': '↑',
      'down': '↓',
      'left': '←',
      'right': '→',
    };

    const lowerKey = key.toLowerCase();
    return keyMap[lowerKey] || key.toUpperCase();
  };

  // Success animation
  if (sequenceStatus === 'success') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -40, scale: 0.8 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
          }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none"
        >
          <motion.div
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 0.3,
            }}
            className="bg-gradient-to-r from-green-500 to-emerald-500 dark:from-green-600 dark:to-emerald-600 backdrop-blur-md border-2 border-green-300 dark:border-green-400 rounded-xl shadow-2xl px-6 py-4 flex items-center gap-3"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 20,
              }}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/30"
            >
              <HiCheckCircle className="w-7 h-7 text-white" />
            </motion.div>
            
            <div className="flex flex-col">
              <span className="text-white font-bold text-lg">Sucesso!</span>
              <span className="text-white/90 text-sm">{successDestination}</span>
            </div>

            {/* Sparkles effect */}
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full"
                initial={{ 
                  x: 0, 
                  y: 0, 
                  opacity: 1,
                  scale: 0 
                }}
                animate={{ 
                  x: (Math.random() - 0.5) * 100,
                  y: (Math.random() - 0.5) * 100,
                  opacity: 0,
                  scale: 1
                }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.1,
                }}
                style={{
                  left: '50%',
                  top: '50%',
                }}
              />
            ))}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Failed animation - Combo Break! (Pixelated Tetris Crumble)
  if (sequenceStatus === 'failed') {
    // Split the notification into fragments (grid-based)
    const fragments = Array.from({ length: 48 }, (_, i) => {
      const cols = 12;
      const row = Math.floor(i / cols);
      const col = i % cols;
      
      // Cascade: top rows fall first, then bottom (reduced delays for faster start)
      const rowDelay = row * 0.04;
      const colDelay = col * 0.01;
      const randomDelay = Math.random() * 0.03;
      
      return {
        id: i,
        gridColumn: col,
        gridRow: row,
        delay: 0.3 + rowDelay + colDelay + randomDelay, // Start after initial shake
        rotate: (Math.random() - 0.5) * 60,
        xOffset: (Math.random() - 0.5) * 100,
        fallDistance: 500 + Math.random() * 200,
      };
    });

    return (
      <AnimatePresence>
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none">
          {/* Full notification shown briefly before breaking */}
          <motion.div
            initial={{ opacity: 1, scale: 1 }}
            animate={{ 
              opacity: [1, 1, 0],
              scale: [1, 1.02, 1],
              x: [0, -4, 4, -4, 4, 0],
              y: [0, -2, 2, -2, 0],
            }}
            transition={{
              duration: 0.5,
              times: [0, 0.6, 1],
            }}
            className="absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 dark:from-red-700 dark:to-orange-700 rounded-lg shadow-2xl px-8 py-5 flex items-center gap-4"
            style={{ width: '400px', height: '100px' }}
          >
            {/* Pixelated X icon */}
            <div className="w-12 h-12 grid grid-cols-6 grid-rows-6 gap-0 flex-shrink-0">
              {[
                [1,0,0,0,0,1],
                [0,1,0,0,1,0],
                [0,0,1,1,0,0],
                [0,0,1,1,0,0],
                [0,1,0,0,1,0],
                [1,0,0,0,0,1],
              ].map((row, i) => 
                row.map((cell, j) => (
                  <div 
                    key={`${i}-${j}`} 
                    className={cell ? 'bg-white' : 'bg-transparent'}
                    style={{ width: '100%', height: '100%' }} 
                  />
                ))
              )}
            </div>
            
            <div className="flex flex-col gap-1">
              <div
                className="text-white font-black text-3xl uppercase tracking-[0.2em] select-none"
                style={{
                  fontFamily: 'monospace',
                  fontWeight: 900,
                  textShadow: '4px 4px 0px rgba(0,0,0,0.8), 2px 2px 0px rgba(0,0,0,0.5)',
                  letterSpacing: '0.15em',
                  lineHeight: 1,
                }}
              >
                COMBO BREAK!
              </div>
              <div 
                className="text-yellow-200 text-sm uppercase tracking-wider"
                style={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
                }}
              >
                ⚠ INVALID SHORTCUT ⚠
              </div>
            </div>
          </motion.div>

          {/* Screen shake container for fragments */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: [0, 0, 1],
            }}
            transition={{
              duration: 0.5,
              times: [0, 0.5, 1],
            }}
            className="relative"
            style={{ width: '400px', height: '100px' }}
          >
            {/* Grid of fragments - each one will fall independently */}
            <div 
              className="absolute inset-0 grid"
              style={{
                gridTemplateColumns: 'repeat(12, 1fr)',
                gridTemplateRows: 'repeat(4, 1fr)',
                gap: '1px',
              }}
            >
              {fragments.map((fragment) => (
                <motion.div
                  key={fragment.id}
                  className="relative overflow-hidden"
                  initial={{ 
                    opacity: 0,
                    y: 0,
                    x: 0,
                    rotate: 0,
                    scale: 1,
                  }}
                  animate={{ 
                    opacity: [0, 1, 1, 0.7, 0.3, 0],
                    y: [0, 0, 150, 300, fragment.fallDistance, fragment.fallDistance + 50],
                    x: [0, 0, fragment.xOffset * 0.3, fragment.xOffset * 0.6, fragment.xOffset, fragment.xOffset * 1.1],
                    rotate: [0, 0, fragment.rotate * 0.2, fragment.rotate * 0.5, fragment.rotate, fragment.rotate * 1.2],
                    scale: [1, 1, 0.98, 0.92, 0.85, 0.7],
                  }}
                  transition={{
                    duration: 1.4,
                    delay: fragment.delay,
                    times: [0, 0.05, 0.3, 0.5, 0.8, 1],
                    ease: "easeIn",
                    opacity: {
                      duration: 1.4,
                      times: [0, 0.05, 0.4, 0.65, 0.85, 1],
                      ease: "linear"
                    }
                  }}
                >
                  {/* Fragment content - a piece of the original notification */}
                  <div 
                    className="w-full h-full bg-gradient-to-br from-red-500 via-red-600 to-orange-600 dark:from-red-600 dark:via-red-700 dark:to-orange-700"
                    style={{
                      imageRendering: 'pixelated' as const,
                      boxShadow: 'inset -1px -1px 2px rgba(0,0,0,0.3)',
                    }}
                  >
                    {/* Render actual content pieces in specific positions */}
                    {fragment.gridRow === 1 && fragment.gridColumn >= 1 && fragment.gridColumn <= 10 && (
                      <div 
                        className="absolute text-white font-black text-xs uppercase"
                        style={{
                          fontFamily: 'monospace',
                          left: `-${fragment.gridColumn * 33.3}px`,
                          top: `-${fragment.gridRow * 25}px`,
                          fontSize: '28px',
                          letterSpacing: '0.15em',
                          textShadow: '3px 3px 0px rgba(0,0,0,0.8)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        COMBO BREAK!
                      </div>
                    )}
                    {fragment.gridRow === 2 && fragment.gridColumn >= 2 && fragment.gridColumn <= 9 && (
                      <div 
                        className="absolute text-yellow-200 text-xs uppercase"
                        style={{
                          fontFamily: 'monospace',
                          left: `-${fragment.gridColumn * 33.3}px`,
                          top: `-${fragment.gridRow * 25}px`,
                          fontSize: '12px',
                          textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ⚠ INVALID SHORTCUT ⚠
                      </div>
                    )}
                    {/* X icon in specific fragments */}
                    {fragment.gridRow >= 0 && fragment.gridRow <= 3 && 
                     fragment.gridColumn >= 0 && fragment.gridColumn <= 1 && (
                      <div 
                        className="absolute"
                        style={{
                          left: `-${fragment.gridColumn * 33.3}px`,
                          top: `-${fragment.gridRow * 25}px`,
                        }}
                      >
                        <div className="w-12 h-12 grid grid-cols-6 grid-rows-6 gap-0">
                          {[
                            [1,0,0,0,0,1],
                            [0,1,0,0,1,0],
                            [0,0,1,1,0,0],
                            [0,0,1,1,0,0],
                            [0,1,0,0,1,0],
                            [1,0,0,0,0,1],
                          ].map((row, i) => 
                            row.map((cell, j) => (
                              <div 
                                key={`${i}-${j}`} 
                                className={cell ? 'bg-white' : 'bg-transparent'}
                                style={{ width: '100%', height: '100%' }} 
                              />
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Explosion particles */}
            {[...Array(16)].map((_, i) => {
              const angle = (i / 16) * Math.PI * 2;
              const distance = 80 + Math.random() * 80;
              return (
                <motion.div
                  key={`particle-${i}`}
                  className="absolute bg-yellow-400"
                  style={{
                    width: '8px',
                    height: '8px',
                    imageRendering: 'pixelated' as const,
                    left: '50%',
                    top: '50%',
                    boxShadow: 'inset -1px -1px 0px rgba(0,0,0,0.3)',
                  }}
                  initial={{ 
                    x: 0, 
                    y: 0, 
                    opacity: 0,
                    scale: 0,
                  }}
                  animate={{ 
                    x: Math.cos(angle) * distance,
                    y: Math.sin(angle) * distance - 30,
                    opacity: [0, 1, 1, 0.5, 0],
                    scale: [0, 1.5, 1, 0.6, 0],
                  }}
                  transition={{
                    duration: 1.0,
                    delay: 0.3 + i * 0.02,
                    times: [0, 0.1, 0.4, 0.7, 1],
                    ease: "easeOut"
                  }}
                />
              );
            })}

            {/* Screen flash */}
            <motion.div
              className="absolute inset-0 bg-white rounded-lg"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 0.6, 0, 0.4, 0],
              }}
              transition={{
                duration: 0.5,
                times: [0, 0.1, 0.2, 0.3, 0.5],
              }}
            />
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  // Normal awaiting state
  return (
    <AnimatePresence>
      {sequenceKeys.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
          }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none"
        >
          <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border border-blue-200 dark:border-blue-800 rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3">
            {/* Icon */}
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50">
              <HiCommandLine className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>

            {/* Keys Display */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Aguardando:
              </span>
              <div className="flex items-center gap-1.5">
                {sequenceKeys.map((key, index) => (
                  <motion.div
                    key={`${key}-${index}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 30,
                      delay: index * 0.05,
                    }}
                  >
                    <kbd className="px-2.5 py-1 text-xs font-semibold text-gray-900 dark:text-gray-100 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-neutral-700 dark:to-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-md shadow-sm min-w-[28px] text-center">
                      {formatKey(key)}
                    </kbd>
                  </motion.div>
                ))}
                
                {/* Separator */}
                <motion.span
                  animate={{
                    opacity: [0.4, 1, 0.4],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="text-blue-500 dark:text-blue-400 font-bold text-sm mx-1"
                >
                  →
                </motion.span>

                {/* Next Key Placeholder */}
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  <kbd className="px-2.5 py-1 text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gradient-to-b from-gray-50/50 to-gray-100/50 dark:from-neutral-700/50 dark:to-neutral-800/50 border border-dashed border-gray-300 dark:border-neutral-600 rounded-md shadow-sm min-w-[28px] text-center">
                    ?
                  </kbd>
                </motion.div>
              </div>
            </div>

            {/* Pulse Effect */}
            <motion.div
              className="absolute inset-0 rounded-xl border-2 border-blue-400/30 dark:border-blue-500/30"
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

